/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the global top bar: history controls, the centred command pill and the brand mark beside it.
 * @state-owner navigation store
 * @allowed-imports React; workspace store; shared icons, marks and hooks
 * @forbidden in-product role switching; API client; module internals
 * @public-api WorkspaceShell
 * @side-effects navigation history moves and overlay opening
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { VispoAnimatedMark } from '../../shared/components/BrandMarks';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RecentIcon,
  SearchIcon,
} from '../../shared/components/VispoIcon';
import { findActivity, findRun } from '../../shared/lib/selectors';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useTranslator } from '../../shared/model/use-workspace';
import type { NavigationSnapshot } from '../../shared/model/workspace-store';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';

/**
 * The role switch is intentionally absent: the student or teacher login URL owns the role.
 */
export interface TopBarNavigationOverride {
  /** Real back navigation for a route (e.g. Settings) the workspace store does not drive. */
  onBack(): void;
  /** Leaves the current route for the snapshot's section instead of a store-only jump. */
  onSelectSnapshot(snapshot: NavigationSnapshot): void;
}

export function TopBar({
  navigation,
  onOpenSearch,
}: {
  navigation?: TopBarNavigationOverride;
  onOpenSearch(): void;
}) {
  const t = useTranslator();
  const storeBack = useWorkspaceUiStore((state) => state.back);
  const forward = useWorkspaceUiStore((state) => state.forward);
  const storeNavigateSnapshot = useWorkspaceUiStore((state) => state.navigateSnapshot);
  const historyIndex = useWorkspaceUiStore((state) => state.historyIndex);
  const historyLength = useWorkspaceUiStore((state) => state.history.length);
  const recent = useWorkspaceUiStore((state) => state.recent);
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentFocusIndex, setRecentFocusIndex] = useState(0);
  const recentTriggerRef = useRef<HTMLButtonElement>(null);
  const recentMenuRef = useRef<HTMLDivElement>(null);
  const recentItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const closeRecent = useCallback(() => {
    setRecentOpen(false);
    setRecentFocusIndex(0);
    window.requestAnimationFrame(() => recentTriggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!recentOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRecent();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [closeRecent, recentOpen]);

  useEffect(() => {
    if (!recentOpen) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (
        target &&
        !recentMenuRef.current?.contains(target) &&
        !recentTriggerRef.current?.contains(target)
      ) {
        closeRecent();
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer, true);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer, true);
  }, [closeRecent, recentOpen]);

  const selectRecent = (snapshot: NavigationSnapshot) => {
    if (navigation) navigation.onSelectSnapshot(snapshot);
    else storeNavigateSnapshot(snapshot);
    closeRecent();
  };

  const recentLabel = (snapshot: NavigationSnapshot) => {
    const section =
      snapshot.section === 'act'
        ? t('活動')
        : snapshot.section === 'room'
          ? t('班級')
          : snapshot.section === 'live'
            ? t('即時')
            : t('報告');
    const open = snapshot.open;
    const openTarget =
      (open?.id &&
        (findActivity(activities, open.id)?.name ?? findRun(runs, open.id)?.activityId)) ||
      undefined;
    const run = findRun(runs, snapshot.selection);
    const selectedActivity = findActivity(activities, snapshot.selection);
    const target =
      (open?.n && (open.k === 'stu' || open.k === 'iv') ? open.n : undefined) ??
      (openTarget ? (findActivity(activities, openTarget)?.name ?? openTarget) : undefined) ??
      selectedActivity?.name ??
      (run ? findActivity(activities, run.activityId)?.name : undefined) ??
      (snapshot.section === 'room' && snapshot.selection?.startsWith('c')
        ? classes[Number(snapshot.selection.slice(1))]?.name
        : snapshot.selection);
    return target ? `${section} · ${target}` : section;
  };

  const moveRecentFocus = (direction: 1 | -1) => {
    if (!recent.length) return;
    const next = (recentFocusIndex + direction + recent.length) % recent.length;
    setRecentFocusIndex(next);
    window.requestAnimationFrame(() => recentItemRefs.current[next]?.focus());
  };

  return (
    <header className="topbar">
      <div className="tbL" style={{ position: 'relative' }}>
        <button
          className="nvb"
          disabled={navigation ? false : historyIndex <= 0}
          id="hbk"
          style={{ minWidth: 44, minHeight: 44 }}
          title={t('上一頁')}
          type="button"
          onClick={navigation ? navigation.onBack : storeBack}
        >
          <ChevronLeftIcon />
        </button>
        <button
          className="nvb"
          // No route this override serves (e.g. Settings) has a forward-navigation concept to
          // offer, so this must stay honestly disabled rather than claim a capability that
          // silently does nothing when clicked.
          disabled={navigation ? true : historyIndex >= historyLength - 1}
          id="hfw"
          style={{ minWidth: 44, minHeight: 44 }}
          title={t('下一頁')}
          type="button"
          onClick={forward}
        >
          <ChevronRightIcon />
        </button>
        <button
          aria-expanded={recentOpen}
          aria-haspopup="menu"
          className="nvb"
          ref={recentTriggerRef}
          style={{ minWidth: 44, minHeight: 44 }}
          title={t('最近')}
          type="button"
          onClick={() => (recentOpen ? closeRecent() : setRecentOpen(true))}
        >
          <RecentIcon />
        </button>
        {recentOpen ? (
          <div
            aria-label={t('最近')}
            className="recent-menu"
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveRecentFocus(1);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveRecentFocus(-1);
              }
            }}
            ref={recentMenuRef}
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              zIndex: 20,
              width: 260,
              padding: 8,
              borderRadius: 12,
              background: 'var(--surface, #fff)',
              color: 'var(--on-surface, #1f2937)',
              boxShadow: '0 12px 32px rgba(0,0,0,.22)',
            }}
          >
            <div style={{ padding: '6px 10px', fontSize: 12, opacity: 0.7 }}>{t('最近開過的')}</div>
            {recent.length ? (
              recent.map((snapshot, index) => (
                <button
                  aria-label={recentLabel(snapshot)}
                  className="recent-menu__item"
                  key={`${snapshot.section}:${snapshot.selection ?? ''}:${JSON.stringify(snapshot.open ?? null)}`}
                  role="menuitem"
                  tabIndex={index === recentFocusIndex ? 0 : -1}
                  ref={(element) => {
                    recentItemRefs.current[index] = element;
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    minHeight: 44,
                    padding: '8px 10px',
                    borderRadius: 8,
                    textAlign: 'left',
                    color: 'inherit',
                  }}
                  type="button"
                  onClick={() => selectRecent(snapshot)}
                >
                  {recentLabel(snapshot)}
                </button>
              ))
            ) : (
              <div style={{ padding: '10px', fontSize: 13, opacity: 0.7 }}>
                {t('目前沒有可搜尋的內容')}
              </div>
            )}
          </div>
        ) : null}
      </div>
      <button className="spill" type="button" onClick={onOpenSearch}>
        <SearchIcon />
        {t('輸入指令或搜尋')}
        <span className="kbd">⌘K</span>
      </button>
      <div className="tbR">
        <span aria-hidden="true" className="tbmark">
          <VispoAnimatedMark />
        </span>
      </div>
    </header>
  );
}
